// Ключи запросов пульта (задача D4): один ключ — один запрос API. Экраны и поток событий (D5) берут ключи только
// отсюда, чтобы «обновить» и «сбросить» попадали в те же данные.
//
// Семейства для сброса по префиксу (`invalidateQueries({ queryKey })`):
// - `['dialogs']` — список диалогов и соседи в разборе (`dialogs`, `neighbors`);
// - `['mail']` — экран «Почта»: список писем (`mailTriage`);
// - `qk.dialog(id)` — разбор диалога и его итог (`dialog`, `dialogSummary`);
// - `['review']` — очереди «На проверку»; `['kb']` — статьи, поиск и пробелы базы; `['metrics']` — ворота и обзор;
// - `['eval']` — прогоны, прогон, оценка стоимости и калибровка судей;
// - `['testbot']` — раздел «Тест-бот» (T19): боты и их тестировщики (`['testbot', 'bots']`), переписки
//   (`['testbot', 'chats']`), переписка, правки и случаи экзамена.
// Фильтры в ключе — объекты из простых значений: TanStack сравнивает их без учёта порядка полей.

/** Параметры запроса в ключе: только простые значения, чтобы ключ сравнивался и сериализовался. */
export type KeyParams = Readonly<Record<string, string | number | boolean | null | undefined | readonly string[]>>;

export const qk = {
  me: ['me'] as const,
  status: ['status'] as const,
  readiness: ['readiness'] as const,
  why: (ticket: string) => ['why', ticket] as const,

  dialogs: (filters: KeyParams) => ['dialogs', 'list', filters] as const,
  neighbors: (id: string, filters: KeyParams) => ['dialogs', 'neighbors', id, filters] as const,
  dialog: (id: string) => ['dialog', id] as const,
  dialogSummary: (id: string) => ['dialog', id, 'summary'] as const,

  /** Экран «Почта» (план MAIL, A9): строки сортировки писем; семейство `['mail']`. */
  mailTriage: (filters: KeyParams) => ['mail', 'triage', filters] as const,

  launch: ['launch'] as const,
  staff: ['staff'] as const,
  /** «Кнопки и стадии сделок» (Ruling 125): не в семействе `['launch']` — изменения «Запуска» отменяют свои запросы. */
  bitrixTargets: ['bitrixTargets'] as const,
  /** «Как Анна квалифицирует» (W3-6): выборы настроек; тоже не в семействе `['launch']`. */
  settingChoices: ['settingChoices'] as const,

  gates: (query: KeyParams) => ['metrics', 'gates', query] as const,
  overview: (query: KeyParams) => ['metrics', 'overview', query] as const,

  reviewCounts: ['review', 'counts'] as const,
  answers: ['review', 'answers'] as const,
  similar: (turnId: string) => ['review', 'similar', turnId] as const,
  gaps: (status: string) => ['kb', 'gaps', status] as const,
  lessons: (status: string) => ['review', 'lessons', status] as const,

  evalRuns: (query: KeyParams) => ['eval', 'runs', query] as const,
  evalRun: (id: string, view: string) => ['eval', 'run', id, view] as const,
  evalEstimate: (query: KeyParams) => ['eval', 'estimate', query] as const,
  calibration: ['eval', 'calibration'] as const,

  kbArticles: (ids: readonly string[]) => ['kb', 'articles', ids] as const,
  kbSearch: (q: string) => ['kb', 'search', q] as const,

  testbotBots: ['testbot', 'bots'] as const,
  testbotBot: (id: string) => ['testbot', 'bots', id] as const,
  testbotTesters: (botId: string) => ['testbot', 'bots', botId, 'testers'] as const,
  testbotChats: (filters: KeyParams) => ['testbot', 'chats', filters] as const,
  testbotChat: (id: string) => ['testbot', 'chat', id] as const,
  testbotCorrections: (state: string) => ['testbot', 'corrections', state] as const,
  testbotExamCases: (status: string) => ['testbot', 'exam', status] as const,
} as const;
